import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { isLocalDashboard } from "../OrgUsageAnalytics/api";
import { useReferralEligibilityEnv } from "./useReferralEligibilityEnv";
import {
  clearReferralOverride,
  createReferralOverride,
  listReferralEligibleOrganizations,
  searchReferralOrganizations,
  syncReferralEligibility,
} from "./api";
import ReferralAnalyticsPanel from "./ReferralAnalyticsPanel";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "./ReferralEligibility.css";

const FORCE_ALLOW = "force_allow";
const FORCE_DENY = "force_deny";

/**
 * Confirm before a localhost dashboard mutates production. Returns true when
 * the caller should proceed.
 * @param {string} env
 * @param {string} actionLabel
 * @return {boolean}
 */
function confirmProdMutationFromLocal(env, actionLabel) {
  if (env !== "prod" || !isLocalDashboard()) {
    return true;
  }

  return window.confirm(
    `You are about to ${actionLabel} against PRODUCTION from a local dashboard. Continue?`,
  );
}

function formatTimestamp(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString();
}

function errorMessage(error) {
  return error?.message || "Something went wrong. Please try again.";
}

/**
 * Small modal for actions that require a typed reason (force_allow / force_deny)
 * or a plain confirmation (reset). Reasons are mandatory when `requireReason`.
 */
function ActionModal({ open, title, description, requireReason, confirmLabel, confirmTone, busy, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const trimmed = reason.trim();
  const disabled = busy || (requireReason && !trimmed);

  return (
    <div className="ref-modal-backdrop" role="dialog" aria-modal="true">
      <div className="ref-modal">
        <h3 className="ref-modal-title">{title}</h3>
        {description ? <p className="ref-modal-description">{description}</p> : null}

        {requireReason ? (
          <label className="ref-modal-label">
            Reason (required)
            <textarea
              className="ref-modal-textarea"
              value={reason}
              maxLength={500}
              placeholder="Why is this change being made?"
              onChange={(event) => setReason(event.target.value)}
              autoFocus
            />
          </label>
        ) : null}

        <div className="ref-modal-actions">
          <button
            type="button"
            className="ref-btn ref-btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`ref-btn ${confirmTone === "danger" ? "ref-btn-danger" : "ref-btn-primary"}`}
            onClick={() => onConfirm(trimmed)}
            disabled={disabled}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Badge describing why an org is (or is not) eligible. */
function StatusBadge({ row }) {
  if (!row.eligible) {
    return <span className="ref-badge ref-badge-denied">Not eligible</span>;
  }

  if (row.override?.type === FORCE_ALLOW) {
    return <span className="ref-badge ref-badge-override">Eligible · Manual</span>;
  }

  return <span className="ref-badge ref-badge-auto">Eligible · Automatic</span>;
}

function OverrideBadge({ override }) {
  if (!override) {
    return <span className="ref-muted">—</span>;
  }

  const label = override.type === FORCE_ALLOW ? "Force allow" : "Force deny";
  const tone = override.type === FORCE_ALLOW ? "ref-badge-override" : "ref-badge-denied";

  return (
    <div className="ref-override-cell">
      <span className={`ref-badge ${tone}`}>{label}</span>
      {override.reason ? (
        <span className="ref-override-reason" title={override.reason}>
          {override.reason}
        </span>
      ) : null}
      {override.createdBy ? (
        <span className="ref-muted ref-override-meta">by {override.createdBy}</span>
      ) : null}
    </div>
  );
}

function AddOrganizationPanel({ env, onAdded }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [pending, setPending] = useState(null);

  const runSearch = async (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) {
      return;
    }

    setSearching(true);
    setSearchError(null);
    try {
      const data = await searchReferralOrganizations(env, trimmed);
      setResults(Array.isArray(data?.results) ? data.results : []);
    } catch (error) {
      setSearchError(errorMessage(error));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const openAdd = (org) => {
    setPending(org);
  };

  const confirmAdd = async (reason) => {
    if (!pending) {
      return;
    }

    setPending((current) => ({ ...current, busy: true }));
    try {
      await createReferralOverride(env, {
        organizationId: pending.organizationId,
        type: FORCE_ALLOW,
        reason,
      });
      toast.success(`Added ${pending.organizationName || pending.organizationId}`);
      setPending(null);
      setResults([]);
      setQuery("");
      onAdded();
    } catch (error) {
      toast.error(errorMessage(error));
      setPending((current) => (current ? { ...current, busy: false } : null));
    }
  };

  return (
    <div className="ref-add-panel">
      <h2 className="ref-section-title">Add an organization</h2>
      <p className="ref-section-subtitle">
        Search by organization name or ID, then grant access with a required
        reason. A manual grant persists until you reset it — the daily sync will
        not remove it.
      </p>

      <form className="ref-search-row" onSubmit={runSearch}>
        <input
          className="ref-input"
          type="text"
          placeholder="Organization name or ID"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button type="submit" className="ref-btn ref-btn-primary" disabled={searching || !query.trim()}>
          {searching ? "Searching…" : "Search"}
        </button>
      </form>

      {searchError ? <div className="ref-inline-error">{searchError}</div> : null}

      {results.length > 0 ? (
        <ul className="ref-search-results">
          {results.map((org) => (
            <li key={org.organizationId} className="ref-search-result">
              <div>
                <div className="ref-result-name">{org.organizationName}</div>
                <div className="ref-muted ref-result-id">{org.organizationId}</div>
              </div>
              <div className="ref-result-status">
                {org.eligible ? (
                  <span className="ref-badge ref-badge-auto">Already eligible</span>
                ) : (
                  <span className="ref-muted">Not eligible</span>
                )}
                <button
                  type="button"
                  className="ref-btn ref-btn-primary ref-btn-sm"
                  onClick={() => openAdd(org)}
                  disabled={org.override?.type === FORCE_ALLOW}
                >
                  {org.override?.type === FORCE_ALLOW ? "Force-allowed" : "Grant access"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <ActionModal
        open={Boolean(pending)}
        title={`Grant referral access`}
        description={
          pending
            ? `Force-allow "${pending.organizationName || pending.organizationId}" (${pending.organizationId}). This persists until reset.`
            : ""
        }
        requireReason
        confirmLabel="Grant access"
        confirmTone="primary"
        busy={Boolean(pending?.busy)}
        onConfirm={confirmAdd}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

function ReferralEligibilityPage({ onReady }) {
  const [env, setEnv] = useReferralEligibilityEnv();
  const [organizations, setOrganizations] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [action, setAction] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [previewFromCriteria, setPreviewFromCriteria] = useState(false);

  useEffect(() => {
    if (onReady) {
      onReady();
    }
  }, [onReady]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listReferralEligibleOrganizations(env);
      setOrganizations(Array.isArray(data?.organizations) ? data.organizations : []);
      setCounts(data?.counts || null);
      setPreviewFromCriteria(Boolean(data?.previewFromCriteria));
    } catch (err) {
      setError(errorMessage(err));
      setOrganizations([]);
      setCounts(null);
      setPreviewFromCriteria(false);
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) {
      return organizations;
    }

    return organizations.filter((row) => {
      const name = (row.organizationName || "").toLowerCase();
      const id = (row.organizationId || "").toLowerCase();
      return name.includes(needle) || id.includes(needle);
    });
  }, [organizations, filter]);

  const runSync = async () => {
    if (!confirmProdMutationFromLocal(env, "sync referral eligibility")) {
      return;
    }

    setSyncing(true);
    try {
      const data = await syncReferralEligibility(env);
      // Backend returns `desiredCount` (post-override eligible set size).
      const desiredCount = data?.summary?.desiredCount;
      toast.success(
        typeof desiredCount === "number"
          ? `Synced ${desiredCount} eligible organization${desiredCount === 1 ? "" : "s"}`
          : "Eligibility sync completed",
      );
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const runAction = async (reason) => {
    if (!action) {
      return;
    }

    const actionLabel =
      action.kind === "reset"
        ? "reset a referral override"
        : action.kind === "allow"
          ? "force-allow an organization"
          : "force-deny an organization";
    if (!confirmProdMutationFromLocal(env, actionLabel)) {
      return;
    }

    setAction((current) => ({ ...current, busy: true }));
    try {
      if (action.kind === "reset") {
        await clearReferralOverride(env, action.row.organizationId);
        toast.success(`Reset ${action.row.organizationName} to automatic eligibility`);
      } else {
        await createReferralOverride(env, {
          organizationId: action.row.organizationId,
          type: action.kind === "allow" ? FORCE_ALLOW : FORCE_DENY,
          reason,
        });
        toast.success(
          `${action.kind === "allow" ? "Force-allowed" : "Removed"} ${action.row.organizationName}`,
        );
      }
      setAction(null);
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
      setAction((current) => (current ? { ...current, busy: false } : null));
    }
  };

  const modalConfig = useMemo(() => {
    if (!action) {
      return { requireReason: false, title: "", description: "", confirmLabel: "", confirmTone: "primary" };
    }

    const name = action.row.organizationName || action.row.organizationId;
    if (action.kind === "reset") {
      return {
        requireReason: false,
        title: "Reset to automatic eligibility",
        description: `Clear the manual override for "${name}". Automatic eligibility resumes on the next daily sync.`,
        confirmLabel: "Reset",
        confirmTone: "primary",
      };
    }
    if (action.kind === "allow") {
      return {
        requireReason: true,
        title: "Force allow",
        description: `Keep "${name}" eligible even if it fails the automatic criteria. Persists until reset.`,
        confirmLabel: "Force allow",
        confirmTone: "primary",
      };
    }

    return {
      requireReason: true,
      title: "Remove from referrals",
      description: `Remove "${name}" and keep it out even if it qualifies automatically. Persists until reset.`,
      confirmLabel: "Remove",
      confirmTone: "danger",
    };
  }, [action]);

  return (
    <div className="ref-page">
      <ToastContainer position="top-right" autoClose={3500} />

      <div className="ref-page-header">
        <div>
          <p className="ref-eyebrow">User & Organization Management</p>
          <h1 className="ref-page-title">Referral Eligibility</h1>
          <p className="ref-page-subtitle">
            Manage which organizations can use the referral button.
          </p>
        </div>
        <div className="ref-header-controls">
          <EnvToggle env={env} onChange={setEnv} />
          <button
            type="button"
            className="ref-btn ref-btn-ghost"
            onClick={runSync}
            disabled={loading || syncing}
          >
            {syncing ? "Syncing…" : "Sync from criteria"}
          </button>
          <button type="button" className="ref-btn ref-btn-ghost" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {previewFromCriteria ? (
        <p className="ref-preview-note">
          Showing organizations that match eligibility criteria (preview). Click{" "}
          <strong>Sync from criteria</strong> to save this list to Firestore.
        </p>
      ) : null}

      {counts ? (
        <div className="ref-counts">
          <div className="ref-count-card">
            <span className="ref-count-value">{counts.eligible}</span>
            <span className="ref-count-label">Eligible</span>
          </div>
          <div className="ref-count-card">
            <span className="ref-count-value">{counts.forceAllow}</span>
            <span className="ref-count-label">Force allow</span>
          </div>
          <div className="ref-count-card">
            <span className="ref-count-value">{counts.forceDeny}</span>
            <span className="ref-count-label">Force deny</span>
          </div>
        </div>
      ) : null}

      <AddOrganizationPanel env={env} onAdded={load} />

      <div className="ref-table-controls">
        <input
          className="ref-input ref-filter"
          type="text"
          placeholder="Filter loaded organizations by name or ID"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <span className="ref-muted">
          {filtered.length} of {organizations.length} shown
        </span>
      </div>

      {error ? <div className="ref-error">{error}</div> : null}

      {loading ? (
        <div className="ref-empty">Loading referral eligibility…</div>
      ) : (
        <div className="ref-table-wrapper">
          {filtered.length === 0 && organizations.length > 0 ? (
            <div className="ref-empty">No organizations match your filter.</div>
          ) : null}
          {filtered.length > 0 ? (
          <table className="ref-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Status</th>
                <th>Override</th>
                <th>Criteria</th>
                <th>Last evaluated</th>
                <th className="ref-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.organizationId}>
                  <td>
                    <div className="ref-org-name">{row.organizationName}</div>
                    <div className="ref-muted ref-org-id">{row.organizationId}</div>
                  </td>
                  <td>
                    <StatusBadge row={row} />
                  </td>
                  <td>
                    <OverrideBadge override={row.override} />
                  </td>
                  <td>
                    {row.eligible && row.source === "computed" ? (
                      <div className="ref-criteria">
                        <span>Team {row.teamSize ?? "—"}</span>
                        <span>Runs {row.checklistRunsLast14Days ?? "—"}</span>
                        <span>Age {row.accountAgeDays ?? "—"}d</span>
                      </div>
                    ) : (
                      <span className="ref-muted">—</span>
                    )}
                  </td>
                  <td>{formatTimestamp(row.evaluatedAt)}</td>
                  <td className="ref-actions-col">
                    <div className="ref-row-actions">
                      {row.override ? (
                        <button
                          type="button"
                          className="ref-btn ref-btn-ghost ref-btn-sm"
                          onClick={() => setAction({ kind: "reset", row })}
                        >
                          Reset
                        </button>
                      ) : null}
                      {row.eligible ? (
                        <button
                          type="button"
                          className="ref-btn ref-btn-danger ref-btn-sm"
                          onClick={() => setAction({ kind: "deny", row })}
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ref-btn ref-btn-primary ref-btn-sm"
                          onClick={() => setAction({ kind: "allow", row })}
                        >
                          Force allow
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          ) : null}
        </div>
      )}

      {env === "prod" ? <ReferralAnalyticsPanel env={env} /> : null}

      <ActionModal
        open={Boolean(action)}
        title={modalConfig.title}
        description={modalConfig.description}
        requireReason={modalConfig.requireReason}
        confirmLabel={modalConfig.confirmLabel}
        confirmTone={modalConfig.confirmTone}
        busy={Boolean(action?.busy)}
        onConfirm={runAction}
        onCancel={() => setAction(null)}
      />
    </div>
  );
}

export default ReferralEligibilityPage;
